import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Mail, Inbox, Trash2, BarChart3, RefreshCw, FileText } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { fetchAdminStats, fetchEnquiries, fetchSubscribers, deleteEnquiry, deleteSubscriber, fetchReportLeads, deleteReportLead } from "../../lib/api";

const fmtDate = (d) => new Date(d).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("enquiries");
  const [stats, setStats] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [subs, setSubs] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [s, e, su, l] = await Promise.all([fetchAdminStats(), fetchEnquiries(), fetchSubscribers(), fetchReportLeads()]);
      setStats(s);
      setEnquiries(e);
      setSubs(su);
      setLeads(l);
    } catch {
      toast.error("Failed to load admin data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onLogout = () => { logout(); navigate("/admin/login"); };

  const removeEnquiry = async (id) => {
    await deleteEnquiry(id);
    setEnquiries((l) => l.filter((x) => x.id !== id));
    toast.success("Enquiry deleted.");
  };
  const removeSub = async (id) => {
    await deleteSubscriber(id);
    setSubs((l) => l.filter((x) => x.id !== id));
    toast.success("Subscriber removed.");
  };
  const removeLead = async (id) => {
    await deleteReportLead(id);
    setLeads((l) => l.filter((x) => x.id !== id));
    toast.success("Lead removed.");
  };

  return (
    <div className="min-h-screen bg-brand-bg">
      <header className="border-b border-brand-line bg-brand-ink text-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 lg:px-8">
          <div>
            <div className="font-heading text-lg font-black tracking-tight">MATRIVON Admin</div>
            <div className="text-xs text-white/50">{user?.email}</div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={load} data-testid="admin-refresh" className="inline-flex items-center gap-1.5 border border-white/20 px-3 py-2 text-sm hover:bg-white/10"><RefreshCw className="h-4 w-4" /> Refresh</button>
            <button onClick={onLogout} data-testid="admin-logout" className="inline-flex items-center gap-1.5 bg-brand-red px-3 py-2 text-sm font-semibold hover:brightness-110"><LogOut className="h-4 w-4" /> Logout</button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-5 py-8 lg:px-8">
        {/* Stats */}
        <div className="mb-8 grid gap-px border border-brand-line bg-brand-line sm:grid-cols-2 lg:grid-cols-4">
          <div className="bg-white p-6" data-testid="stat-enquiries">
            <Inbox className="h-6 w-6 text-brand-blue" />
            <div className="mt-3 font-mono text-3xl font-bold text-brand-ink">{stats?.enquiries ?? "—"}</div>
            <div className="text-sm text-brand-muted">Total enquiries</div>
          </div>
          <div className="bg-white p-6" data-testid="stat-report-leads">
            <FileText className="h-6 w-6 text-brand-blue" />
            <div className="mt-3 font-mono text-3xl font-bold text-brand-ink">{stats?.report_leads ?? "—"}</div>
            <div className="text-sm text-brand-muted">Report leads</div>
          </div>
          <div className="bg-white p-6" data-testid="stat-subscribers">
            <Mail className="h-6 w-6 text-brand-blue" />
            <div className="mt-3 font-mono text-3xl font-bold text-brand-ink">{stats?.subscribers ?? "—"}</div>
            <div className="text-sm text-brand-muted">Subscribers</div>
          </div>
          <div className="bg-white p-6" data-testid="stat-events">
            <BarChart3 className="h-6 w-6 text-brand-blue" />
            <div className="mt-3 font-mono text-3xl font-bold text-brand-ink">{stats?.events?.reduce((a, b) => a + b.count, 0) ?? "—"}</div>
            <div className="text-sm text-brand-muted">Tracked events</div>
          </div>
        </div>

        {/* Event breakdown */}
        {stats?.events?.length > 0 && (
          <div className="mb-8 flex flex-wrap gap-2">
            {stats.events.map((e) => (
              <span key={e.event} className="border border-brand-line bg-white px-3 py-1.5 text-xs text-brand-ink">
                {e.event}: <span className="font-mono font-semibold text-brand-blue">{e.count}</span>
              </span>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div className="mb-4 flex flex-wrap gap-2">
          <button onClick={() => setTab("enquiries")} data-testid="tab-enquiries" className={`border px-4 py-2 text-sm font-medium ${tab === "enquiries" ? "border-brand-blue bg-brand-blue text-white" : "border-brand-line bg-white text-brand-muted"}`}>Enquiries ({enquiries.length})</button>
          <button onClick={() => setTab("leads")} data-testid="tab-leads" className={`border px-4 py-2 text-sm font-medium ${tab === "leads" ? "border-brand-blue bg-brand-blue text-white" : "border-brand-line bg-white text-brand-muted"}`}>Report Leads ({leads.length})</button>
          <button onClick={() => setTab("subscribers")} data-testid="tab-subscribers" className={`border px-4 py-2 text-sm font-medium ${tab === "subscribers" ? "border-brand-blue bg-brand-blue text-white" : "border-brand-line bg-white text-brand-muted"}`}>Subscribers ({subs.length})</button>
        </div>

        {loading ? (
          <div className="py-16 text-center text-brand-muted">Loading…</div>
        ) : tab === "enquiries" ? (
          <div className="space-y-4" data-testid="enquiries-list">
            {enquiries.length === 0 && <div className="border border-brand-line bg-white p-8 text-center text-brand-muted">No enquiries yet.</div>}
            {enquiries.map((e) => (
              <div key={e.id} className="border border-brand-line bg-white p-6">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="font-heading text-lg font-bold text-brand-ink">{e.name} {e.company && <span className="text-brand-muted">· {e.company}</span>}</div>
                    <div className="mt-0.5 text-sm text-brand-muted">
                      <a href={`mailto:${e.email}`} className="text-brand-blue hover:underline">{e.email}</a>
                      {e.country && <> · {e.country}</>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="border border-brand-line bg-brand-bg px-2.5 py-1 text-xs font-medium text-brand-blue">{e.area_of_interest}</span>
                    <button onClick={() => removeEnquiry(e.id)} data-testid={`delete-enquiry-${e.id}`} className="text-brand-muted hover:text-brand-red"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
                <p className="mt-3 whitespace-pre-wrap border-l-2 border-brand-line pl-4 text-sm text-brand-ink/85">{e.message}</p>
                <div className="mt-3 text-xs text-brand-muted">{fmtDate(e.created_at)}</div>
              </div>
            ))}
          </div>
        ) : tab === "leads" ? (
          <div className="border border-brand-line bg-white" data-testid="leads-list">
            {leads.length === 0 && <div className="p-8 text-center text-brand-muted">No report leads yet.</div>}
            {leads.map((l) => (
              <div key={l.id} className="flex flex-wrap items-center justify-between gap-3 border-b border-brand-line px-6 py-4 last:border-0">
                <div>
                  <div className="font-medium text-brand-ink">{l.name} <span className="text-brand-muted">· {l.company || "—"}</span></div>
                  <div className="mt-0.5 text-sm text-brand-muted">
                    <a href={`mailto:${l.email}`} className="text-brand-blue hover:underline">{l.email}</a>
                  </div>
                  <div className="mt-1 text-xs text-brand-muted">{l.report_title || l.report_slug} · {fmtDate(l.created_at)}</div>
                </div>
                <button onClick={() => removeLead(l.id)} data-testid={`delete-lead-${l.id}`} className="text-brand-muted hover:text-brand-red"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        ) : (
          <div className="border border-brand-line bg-white" data-testid="subscribers-list">
            {subs.length === 0 && <div className="p-8 text-center text-brand-muted">No subscribers yet.</div>}
            {subs.map((s) => (
              <div key={s.id} className="flex items-center justify-between border-b border-brand-line px-6 py-3 last:border-0">
                <div>
                  <span className="text-sm font-medium text-brand-ink">{s.email}</span>
                  <span className="ml-3 text-xs text-brand-muted">{s.source} · {fmtDate(s.created_at)}</span>
                </div>
                <button onClick={() => removeSub(s.id)} data-testid={`delete-sub-${s.id}`} className="text-brand-muted hover:text-brand-red"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
